import { action, observable } from "mobx";
import { IObjectMap } from "interfaces/common";
import {
  ADD_NEW_ROW,
  DELETE_LOGISTIC_EVENT,
  DELETE_LOGISTIC_FILE,
  DOWNLOAD_LOGISTIC_FILE,
  REMOVE_EDITING_KEY,
  SAVE_LOGISTIC_ENTITY,
  SET_EDITING_KEY,
  UPDATE_LOGISTIC_ENTITY,
} from "constants/events";
import ee from "../services/EventEmitter";
import AbstractLoaderStore from "./pages/AbstractLoaderStore";
import { Logistic } from "models/Logistic";
import CountriesStore from "stores/CountriesStore";
import { SelectOption } from "components/common/forms/Interfaces";
import { message, Modal } from "antd";
import { MODAL } from "constants/translates";
import {
  addNewLogisticRow,
  EXPORT_TO_EXCEL_TIMEOUT_MESSAGE,
  newRowIndex,
} from "components/tables/declarantTable/constants";
import moment from "moment/moment";
import getFileNameFromContentDisposition from "helpers/parsers";
import downloadFile from "helpers/downloadFile";
import { Pagination } from "models/Pagination";

const logisticDateRange = {
  start_date: moment().subtract(20, "days"),
  end_date: moment().subtract(-10, "days"),
};

const defaultPagination = {
  total: 0,
  per_page: 0,
  current_page: 0,
  last_page: 999,
  from: 0,
  to: 0,
};

const defaultParameters = {
  ...logisticDateRange,
  page: 0,
};

interface DeleteRowProps {
  entityId: unknown;
  isTemporary: boolean;
  children?: Logistic[];
}

interface UpdateRowProps {
  savingData: unknown[];
  entityId: string;
  documents: File[];
}

interface SaveUploadFileParameters {
  id: string;
  documents: File[];
}
export interface DownloadFileParameters {
  id: string;
  documents: string;
}

type Row = string | number;

export type SearchInputValue = {
  value: string;
};

class LogisticStore extends AbstractLoaderStore {
  countryStore: CountriesStore;

  constructor() {
    super();

    this._parameters = defaultParameters;
    this.countryStore = new CountriesStore();

    ee.on(DELETE_LOGISTIC_EVENT, ({ entityId, isTemporary, children }) => {
      this.deleteLogisticRow({ entityId, isTemporary, children });
    });

    ee.on(SET_EDITING_KEY, (key: number) => {
      this.downloadSearchOptions();
      this.setEditingKey(key);
    });

    ee.on(REMOVE_EDITING_KEY, (key: number) => {
      this.removeEditingKey(key);
    });

    ee.on(ADD_NEW_ROW, () => {
      this.downloadSearchOptions();
      this.addNewRow();
    });

    ee.on(SAVE_LOGISTIC_ENTITY, ({ savingData, entityId, documents }) => {
      this.saveLogisticEntity(savingData, documents);
      this.removeEditingKey(entityId);
    });

    ee.on(UPDATE_LOGISTIC_ENTITY, ({ savingData, entityId, documents }) => {
      this.updateLogisticEntity({ savingData, entityId, documents });
      this.removeEditingKey(entityId);
    });

    ee.on(DOWNLOAD_LOGISTIC_FILE, ({ documents, id }) => {
      this.downloadFile({ documents, id });
    });

    ee.on(DELETE_LOGISTIC_FILE, ({ documents, id }) => {
      this.deleteFile({ documents, id });
    });
  }

  @observable
  selectedRows: Row[] = [];

  @observable
  expandedRows: Row[] = [];

  @observable
  editingKeys: number[] = [];

  @observable
  countryOptions: SelectOption[] = [];

  @observable
  tcOptions: SearchInputValue[] = [];

  @observable
  contractorOptions: SearchInputValue[] = [];

  @observable
  hasMoreItems = false;

  protected _pagination: Pagination = defaultPagination;

  @observable
  isLoadingButton = false;

  @action
  load(parameters: IObjectMap | null, needUpdateData = false): void {
    if (parameters) {
      this._parameters = {
        ...this._parameters,
        ...parameters,
        page: needUpdateData ? 1 : this._pagination.current_page + 1,
      };
    }

    this.loadTableData(needUpdateData);
  }

  @action
  private loadTableData(refresh = false): void {
    if (!this.hasMoreItems && !refresh) {
      return;
    }

    this.isLoadingTableData = true;

    this.hasMoreItems = false;

    this.loadLogisticByClients()
      .then(async (response) => {
        const items = await response.json();

        this._data = refresh
          ? items.data
          : this._data.concat(Object.values(items.data));

        const page = items.current_page as number;
        const lastPage = items.last_page as number;

        this._pagination = {
          total: items.total,
          per_page: items.per_page,
          current_page: page,
          last_page: lastPage,
          from: items.from,
          to: items.to,
        };

        this.hasMoreItems = page < lastPage;
        this.changeSelectedRows([]);
      })
      .catch(() => {
        this._data = [];
        this._pagination = {
          total: 0,
          per_page: 0,
          current_page: 0,
          last_page: 0,
          from: 0,
          to: 0,
        };
      })
      .finally(() => {
        this.isLoadingTableData = false;
      });
  }

  protected loadLogisticByClients(): Promise<Response> {
    return this.apiManager.loadLogisticByClients(this._parameters);
  }

  @action
  setEditingKey(key: number): void {
    this.editingKeys = [...this.editingKeys, key];
  }

  @action
  removeEditingKey(key: number): void {
    this.editingKeys = this.editingKeys.filter(
      (editingKey) => editingKey !== key
    );
  }

  @action
  changeSelectedRows(ids: Row[]): void {
    this.selectedRows = ids;
  }

  getLoadResponse(parameters: IObjectMap): Promise<Response> {
    return this.apiManager.loadLogisticByClients(parameters);
  }

  get countries(): SelectOption[] {
    if (this.countryOptions.length === 0) {
      this.countryOptions = this.countryStore.all.map<SelectOption>(
        (country) => ({
          label: `${country.value} (${country.name})` || "",
          value: country.value?.toLowerCase() || 0,
        })
      );
    }

    return this.countryOptions;
  }

  @action
  private addNewRow() {
    if (this.selectedRows.length > 1) {
      return;
    }

    if (this.selectedRows.length === 1) {
      const rowId = this.selectedRows[0];
      const expendedIdCheck = this.expandedRows?.some((id) => id === rowId);

      if (!expendedIdCheck) {
        this.expandedRows = [...this.expandedRows, rowId];
      }

      const newRowData = (this._data as Logistic[]).find(
        (row) => row.id === rowId
      );

      const withTemporary = (this._data as Logistic[]).some((row) => {
        if (row.children && row.children.length > 0) {
          return row.children.some((child) => child.temporary);
        }

        return row.temporary;
      });

      if (!withTemporary) {
        this._data = (this._data as Logistic[]).map((row) => {
          if (row.id === rowId && row.children) {
            const newData = Object.assign({}, newRowData);
            delete newData?.children;

            row.children.push({
              ...newData,
              temporary: true,
              id: newRowIndex,
              parent_id: parseInt(row.id as string),
              documents: "",
            });
          }

          return row;
        });

        this.setEditingKey(newRowIndex);
      }

      return;
    }

    const withTemporary = (this._data as Logistic[]).some(
      (row) => row.temporary
    );

    if (!withTemporary) {
      this._data = [...this._data, addNewLogisticRow];

      this.setEditingKey(newRowIndex);
    }
  }

  protected deleteLogisticRow({
    entityId,
    isTemporary,
    children,
  }: DeleteRowProps): void {
    if (children && children.length > 0) {
      Modal.info({
        title: MODAL.info_title,
        content: MODAL.info_delete_checker,
        onOk() {},
      });

      return;
    }

    if (isTemporary) {
      const dataWithoutTemporaryChildren = (this._data as Logistic[]).map(
        (row) => {
          if (row?.children && row?.children.length > 0) {
            const children = row?.children.filter((child) => !child.temporary);

            return { ...row, children };
          }

          return row;
        }
      );

      this._data = dataWithoutTemporaryChildren.filter(
        (row) => !row?.temporary
      );

      return;
    }

    this.deleteLogisticEntity(entityId);
  }

  protected saveLogisticEntity(values: unknown[], documents: File[]): void {
    this.apiManager
      .saveLogisticEntity({
        ...values,
      })
      .then(async (response) => {
        const data = await response.json();

        if (data?.entity?.id && documents) {
          const saveFilesParams = {
            documents,
            id: data?.entity?.id,
          };

          this.saveLogisticDocuments(saveFilesParams);
        }

        message.success(data?.message);
      })
      .finally(() => {
        this.refresh();
      });
  }

  protected updateLogisticEntity({
    savingData,
    entityId,
    documents,
  }: UpdateRowProps): void {
    this.apiManager
      .updateLogisticEntity(entityId, {
        ...savingData,
      })
      .then(async (response) => {
        const data = await response.json();

        if (entityId && documents) {
          const saveFilesParams = {
            documents,
            id: entityId,
          };

          this.saveLogisticDocuments(saveFilesParams);
        }
        message.success(data.message);
      })
      .finally(() => {
        this.refresh();
      });
  }

  protected deleteLogisticEntity(entityId: unknown): void {
    this.apiManager
      .deleteLogisticEntity(entityId)
      .then(async (response) => {
        const data = await response.json();

        message.success(data.message);
      })
      .finally(() => {
        this.refresh();
      });
  }
  @action
  saveLogisticDocuments(parameters: SaveUploadFileParameters) {
    const { documents, id } = parameters;
    const formData = new FormData();

    formData.append("id", id);
    documents?.forEach((file: File) => formData.append("documents[]", file));

    this.apiManager
      .saveLogisticDocuments(formData)
      .then(async (response) => {
        const data = await response.json();

        if (data?.error) {
          message.error(data.error);
        }

        if (data?.message) {
          message.success(data.message);
        }
      })
      .finally(() => {
        this.refresh();
      });
  }

  @action
  downloadFile(parameters: DownloadFileParameters) {
    this.apiManager
      .downloadFile({ ...parameters })
      .then((response) => {
        if (parameters?.documents === "all") {
          const fileName = getFileNameFromContentDisposition(
            response.headers.get("Content-Disposition")
          );

          response.blob().then((blob) => {
            downloadFile(blob, fileName);
          });

          return;
        }

        response.blob().then((blob: Blob) => {
          const fileURL = window.URL.createObjectURL(blob);
          const alink = document.createElement("a");

          alink.href = fileURL;
          alink.download = parameters?.documents;
          alink.click();
        });
      })
      .catch(async (response) => {
        const data = await response.json();
        message.error(data.error);
      });
  }

  @action
  deleteFile(parameters: DownloadFileParameters) {
    this.apiManager.deleteLogisticFile(parameters).then(() => {
      this.refresh();
    });
  }

  prepareOptionValues(data: string[]): SearchInputValue[] {
    const optionValues = data.map((value: string) => {
      return {
        value,
      };
    });

    return [
      ...new Map(
        optionValues.map((item: SearchInputValue) => [item["value"], item])
      ).values(),
    ] as SearchInputValue[];
  }

  tcInputValues(): void {
    this.apiManager.getSearchFieldValues("tc").then(async (response) => {
      const data = await response.json();

      this.tcOptions = this.prepareOptionValues(data);
    });
  }

  contractorInputValues(): void {
    this.apiManager
      .getSearchFieldValues("contractor")
      .then(async (response) => {
        const data = await response.json();

        this.contractorOptions = this.prepareOptionValues(data);
      });
  }

  downloadSearchOptions(): void {
    this.tcInputValues();
    this.contractorInputValues();
  }

  downloadExcel(params: IObjectMap): void {
    if (this.isLoadingButton) {
      return;
    }

    this.isLoadingButton = true;

    this.apiManager
      .downloadLogisticAsExcelFile(params)
      .then((response) => {
        const fileName = getFileNameFromContentDisposition(
          response.headers.get("Content-Disposition")
        );

        response.blob().then((blob) => {
          downloadFile(blob, fileName);
        });
      })
      .catch(async (response) => {
        if (Object.keys(response).length == 0) {
          message.error(EXPORT_TO_EXCEL_TIMEOUT_MESSAGE);
          return;
        }
        const data = await response.json();

        message.error(data.message);
      })
      .finally(() => {
        this.isLoadingButton = false;
      });
  }
}

export default new LogisticStore();
